import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import BreadMe from "../lib/Breadme";
import useUserList from "../../hooks/useUserrList";
import AddressSelect from "../lib/AddressSelect";
import FarmerEdit from "./FarmerEdit";
import UserContext from "../../providers/UserContext";
import config from "../../config";
import { Link } from "react-router-dom";
import * as querystring from "querystring";
import NetworkApi from "../../network/networkApi";
import moment from "moment";

interface BatchContractFarmer {
  rd: string;
  name: string;
}

const Farmers = () => {
  const { user } = useContext(UserContext);
  const canEdit = useMemo(() => {
    if (user.role?.cd == "ADMIN" || user?.role?.cd == "VILLAGE_AGRONOMIST") {
      return true;
    }
    return false;
  }, [user]);
  const [content, loadContent, changeContentFilter, filter] = useUserList(1);
  const [filterType, setFilterType] = useState("address");
  const [farmerEdit, setFarmerEdit] = useState({
    visible: false,
    id: null,
  });
  const [batchContractModal, setBatchContractModal] = useState<{
    visible: boolean;
    farmer?: BatchContractFarmer;
  }>({
    visible: false,
  });

  useEffect(() => {
    changeContentFilter((c: any) => {
      return { ...c, filterType, page: 1 };
    });
  }, [filterType, changeContentFilter]);

  const handleParam = (pagination: any, filters: any, sorter: any) => {
    changeContentFilter((c: any) => {
      return { ...c, page: pagination.current, filterType };
    });
  };

  const openBatchContractModal = (farmer: BatchContractFarmer) => {
    if (!farmer.rd) {
      message.error("Регистр олдсонгүй");
      return;
    }
    setBatchContractModal({ visible: true, farmer });
  };

  const renderBatchContract = (
    rd: string,
    name: string,
    hasBatchContract?: boolean,
  ) => {
    if (hasBatchContract) {
      return user.role?.cd === "ADMIN" ? (
        <Space size={4}>
          <Tag color="blue">Багц</Tag>
          <Button
            type="link"
            size="small"
            onClick={() => openBatchContractModal({ rd, name })}
          >
            Тохируулах
          </Button>
        </Space>
      ) : (
        <Tag color="blue">Багц</Tag>
      );
    }
    return user.role?.cd === "ADMIN" ? (
      <Button
        type="primary"
        ghost
        size="small"
        onClick={() => openBatchContractModal({ rd, name })}
      >
        Тохируулах
      </Button>
    ) : "-";
  };

  const columnsByAddress = [
    {
      title: "№",
      dataIndex: "id",
      width: 50,
    },
    {
      title: "Нэр",
      render: (item: any) => {
        return item.organizations?.length > 0 ? item.organizations[0].name : "";
      },
    },
    {
      title: "Регистр",
      render: (item: any) => {
        return item.organizations?.length > 0 ? item.organizations[0].rd : "";
      },
    },
    {
      title: "Харьяалал",
      render: (item: any) => {
        return `${item.aimag?.name || ""} ${item.sum?.name || ""}`;
      },
    },
    {
      title: "Төрөл",
      render: (item: any) => {
        return item.organizations?.length > 0
          ? item.organizations[0]?.type == "farmer"
            ? "Иргэн"
            : "Хуулийн этгээд"
          : "";
      },
    },
    {
      title: "АШҮЭ код",
      render: (item: any) => {
        return item.organizations?.length > 0 ? item.organizations[0].aid : "";
      },
    },
    {
      title: "Багцын гэрээ",
      render: (item: any) => {
        const organization = item.organizations?.[0];
        return organization
          ? renderBatchContract(
              organization.rd,
              organization.name,
              organization.hasBatchContract,
            )
          : "-";
      },
    },
    {
      title: "Утас",
      dataIndex: "phone",
    },
    {
      title: "Тоног төхөөрөмж",
      dataIndex: "equipment",
    },
    {
      title: "Баталгаажсан төлөв",
      dataIndex: "confirm",
      render: (value: number) => (
        <>
          {
            {
              no: <Tag color="red">Цуцалсан</Tag>,
              yes: <Tag color="green">Баталгаажсан</Tag>,
              inactive: <Tag color="default">Идэвхгүй</Tag>,
            }[value]
          }
        </>
      ),
    },
    {
      title: "Үйлдэл",
      render: (index: any) => (
        <Space size={10}>
          <Link to={"/farmers/" + index.id}>
            <Button
              type={"primary"}
              size={"small"}
              ghost
              icon={<FileTextOutlined />}
            />
          </Link>
          {canEdit ? (
            <Button
              onClick={() => {
                console.log(index);
                setFarmerEdit({ visible: true, id: index.id });
              }}
              type={"primary"}
              size={"small"}
              ghost
            >
              <EditOutlined />
            </Button>
          ) : null}
        </Space>
      ),
      ellipsis: true,
    },
  ];

  const columnsByUsage = [
    {
      title: "№",
      dataIndex: "id",
      width: 50,
    },
    {
      title: "Нэр",
      render: (item: any) => {
        return item.name;
      },
    },
    {
      title: "Регистр",
      render: (item: any) => {
        return item.rd;
      },
    },
    {
      title: "Харьяалал",
      render: (item: any) => {
        return `${item.aimag_name || ""} ${item.sum_name || ""}`;
      },
    },
    {
      title: "Төрөл",
      render: (item: any) => {
        return item.type == "farmer" ? "Иргэн" : "Хуулийн этгээд";
      },
    },
    {
      title: "АШҮЭ код",
      render: (item: any) => {
        return item.aid;
      },
    },
    {
      title: "Багцын гэрээ",
      render: (item: any) =>
        renderBatchContract(item.rd, item.name, item.hasBatchContract),
    },
    {
      title: "Утас",
      dataIndex: "phone",
    },
    {
      title: "Тоног төхөөрөмж",
      dataIndex: "equipment",
    },
    {
      title: "Баталгаажсан төлөв",
      dataIndex: "confirm",
      render: (value: number) => (
        <>
          {
            {
              no: <Tag color="red">Цуцалсан</Tag>,
              yes: <Tag color="green">Баталгаажсан</Tag>,
              inactive: <Tag color="default">Идэвхгүй</Tag>,
            }[value]
          }
        </>
      ),
    },
    {
      title: "Үйлдэл",
      render: (index: any) => (
        <Space size={10}>
          {canEdit ? (
            <Button
              onClick={() => {
                console.log(index);
                setFarmerEdit({ visible: true, id: index.user_id });
              }}
              type={"primary"}
              size={"small"}
              ghost
            >
              <EditOutlined />
            </Button>
          ) : null}
        </Space>
      ),
      ellipsis: true,
    },
  ];

  return (
    <>
      <BreadMe title={["Тариаланчид"]} />
      <Layout.Content
        style={{ background: "#fff", padding: 24, margin: 0, minHeight: 280 }}
      >
        <Form
          onFinish={(values) => {
            changeContentFilter({
              ...values,
              filterType,
            });
          }}
          name={"farmers-filter"}
          layout="inline"
        >
          <AddressSelect hideLabel={true} ignoreBag />
          <Form.Item name={"name"}>
            <Input placeholder={"Нэр, регистр"} />
          </Form.Item>
          <Form.Item name={"type"} initialValue="">
            <Select style={{ width: 150 }}>
              <Select.Option value="">Төрөл</Select.Option>
              <Select.Option value="farmer">Хувь хүн</Select.Option>
              <Select.Option value="organization">Хуулийн этгээд</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name={"confirm"} initialValue="">
            <Select>
              <Select.Option value="">Баталгаажсан төлөв</Select.Option>
              <Select.Option value="null">Баталгаажуулаагүй</Select.Option>
              <Select.Option value="yes">Баталгаажсан</Select.Option>
              <Select.Option value="no">Цуцалсан</Select.Option>
              <Select.Option value="inactive">Идэвхгүй</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name={"batchContract"} initialValue="">
            <Select style={{ width: 170 }}>
              <Select.Option value="">Багцын гэрээ</Select.Option>
              <Select.Option value="yes">Багцын гэрээтэй</Select.Option>
              <Select.Option value="no">Багцын гэрээгүй</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name={"search"}>
            <Space size={16}>
              <Button type={"primary"} htmlType="submit">
                <SearchOutlined />
              </Button>
              {user?.role.cd == "ADMIN" && (
                <Button
                  target="blank"
                  href={`${config.get(
                    "apiUrl"
                  )}/api/user/list/download?${querystring.stringify(filter)}`}
                  type={"primary"}
                >
                  <DownloadOutlined />
                  Татах
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
        <Descriptions title="Тариаланчид" style={{ marginTop: 15 }}>
          <Descriptions.Item label="Нийт">{content.count}</Descriptions.Item>
        </Descriptions>
        <Row style={{ marginTop: 15, marginBottom: 15 }}>
          <Col span={24}>
            <Tabs
              defaultActiveKey="address"
              type="card"
              onChange={(filterType) => {
                setFilterType(filterType);
              }}
            >
              <Tabs.TabPane tab="Харьяалалын" key="address">
                <Table
                  scroll={{ x: true }}
                  loading={content.loading}
                  columns={columnsByAddress}
                  dataSource={content.users ?? []}
                  pagination={{
                    pageSize: 10,
                    current: content.page,
                    total: content.count,
                  }}
                  onChange={handleParam}
                  rowKey={"id"}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Ү.а явуулдаг" key="usage">
                <Table
                  scroll={{ x: true }}
                  loading={content.loading}
                  columns={columnsByUsage}
                  dataSource={content.orgs ?? []}
                  pagination={{
                    pageSize: 10,
                    current: content.page,
                    total: content.count,
                  }}
                  onChange={handleParam}
                  rowKey={"id"}
                />
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
        <FarmerEdit
          visible={farmerEdit.visible}
          id={farmerEdit.id}
          onHide={() => {
            loadContent();
            setFarmerEdit({
              visible: false,
              id: null,
            });
          }}
        />
        <BatchContractModal
          visible={batchContractModal.visible}
          farmer={batchContractModal.farmer}
          onCancel={() => {
            setBatchContractModal({ visible: false });
          }}
        />
      </Layout.Content>
    </>
  );
};

interface BatchContractModalProps {
  visible: boolean;
  farmer?: BatchContractFarmer;
  onCancel: () => void;
}

const BatchContractModal = ({
  visible,
  farmer,
  onCancel,
}: BatchContractModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const loadBatchContract = useCallback(
    async (values?: any) => {
      if (!visible || !farmer?.rd) {
        return;
      }

      const currentValues = form.getFieldsValue();
      const year = values?.year || currentValues.year || moment();
      const incentiveType =
        values?.incentiveType || currentValues.incentiveType || "vegetable";

      setLoading(true);
      const response = await NetworkApi.requestGet(
        "/api/incentive/batch-contract/detail",
        {
          rd: farmer.rd,
          year: moment(year).format("YYYY"),
          incentiveType,
        },
      );
      setLoading(false);

      if (response.resultCode === 200) {
        form.setFieldsValue({
          hasBatchContract: !!response.result?.hasBatchContract,
        });
      } else {
        form.setFieldsValue({ hasBatchContract: false });
        message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
      }
    },
    [visible, farmer?.rd, form],
  );

  useEffect(() => {
    if (visible && farmer) {
      const initialValues = {
        name: farmer.name,
        rd: farmer.rd,
        year: moment(),
        incentiveType: "vegetable",
        hasBatchContract: false,
      };
      form.setFieldsValue(initialValues);
      loadBatchContract(initialValues);
    }
  }, [visible, farmer, form, loadBatchContract]);

  const onFinish = useCallback(
    async (values: any) => {
      if (!farmer?.rd) {
        message.error("Регистр олдсонгүй");
        return;
      }

      setLoading(true);
      const response = await NetworkApi.requestPost(
        "/api/incentive/batch-contract/update",
        {
          rd: farmer.rd,
          year: moment(values.year).format("YYYY"),
          incentiveType: values.incentiveType,
          hasBatchContract: !!values.hasBatchContract,
        },
      );
      setLoading(false);

      if (response.resultCode === 200) {
        message.success("Амжилттай хадгалагдлаа");
        onCancel();
      } else {
        message.error(response.message || "Алдаа гарлаа, дахин оролдоно уу!");
      }
    },
    [farmer, onCancel],
  );

  return (
    <Modal
      width={700}
      visible={visible}
      title="Багцын гэрээ"
      onCancel={onCancel}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changedValues, values) => {
          if (changedValues.year || changedValues.incentiveType) {
            loadBatchContract(values);
          }
        }}
      >
        <Row gutter={15}>
          <Col span={12}>
            <Form.Item name="name" label="Тариаланч">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="rd" label="Регистр">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="incentiveType"
              label="Урамшуулалын төрөл"
              rules={[{ required: true, message: "Урамшуулалын төрөл сонгоно уу" }]}
            >
              <Select placeholder="Урамшуулалын төрөл сонгоно уу">
                <Select.Option value="vegetable">Хүнсний ногоо</Select.Option>
                <Select.Option value="wheat">Улаанбуудай</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="year"
              label="Он"
              rules={[{ required: true, message: "Он сонгоно уу" }]}
            >
              <DatePicker picker="year" placeholder="Он" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="hasBatchContract"
              label="Багцын гэрээ"
              valuePropName="checked"
            >
              <Checkbox disabled={loading}>Багцын гэрээтэй</Checkbox>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Хадгалах
              </Button>
              <Button type="default" onClick={onCancel}>
                Болих
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Farmers;
